import { X, ExternalLink, Clock } from 'lucide-react'
import type { StationWithDistance, FuelType } from '@fuelfinder/shared'
import { useStationHistory } from '../hooks'
import { PriceHistoryChart } from './PriceHistoryChart'

interface StationDetailSheetProps {
  station: StationWithDistance
  fuelType: 'E10' | 'B7'
  pricePercentile: number
  onClose: () => void
}

/**
 * Get Tailwind text color class based on price percentile
 */
function getPriceColorClass(percentile: number): string {
  if (percentile <= 25) return 'text-green-600'
  if (percentile <= 75) return 'text-amber-500'
  return 'text-red-500'
}

/**
 * Format price in pence to display format (e.g., 131.9p)
 */
function formatPrice(pricePence: number | null): string {
  if (pricePence === null) return 'N/A'
  return `${pricePence.toFixed(1)}p`
}

/**
 * Format distance in miles
 */
function formatDistance(miles: number): string {
  if (miles < 0.1) return '< 0.1 mi'
  return `${miles.toFixed(1)} mi`
}

/**
 * Get Google Maps directions URL
 */
function getDirectionsUrl(lat: number, lng: number): string {
  return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`
}

/**
 * Format date string to human-readable relative time
 */
function formatRelativeTime(isoString: string): string {
  try {
    const date = new Date(isoString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins} min${diffMins === 1 ? '' : 's'} ago`
    if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`
    if (diffDays < 7) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`

    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
    })
  } catch {
    return isoString
  }
}

/**
 * Fuel type labels for display
 */
const FUEL_LABELS: Record<FuelType, string> = {
  E10: 'Petrol',
  B7: 'Diesel',
  E5: 'Super',
  SDV: 'Premium Diesel',
}

/**
 * Bottom sheet showing station details when a marker is clicked
 */
export function StationDetailSheet({
  station,
  fuelType,
  pricePercentile,
  onClose,
}: StationDetailSheetProps) {
  const price = station.prices[fuelType]
  const priceColorClass = getPriceColorClass(pricePercentile)
  const { history, isLoading: historyLoading, timeRange, setTimeRange } = useStationHistory(station.id)

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[1001] bg-white dark:bg-slate-900 rounded-t-2xl shadow-2xl max-h-[60vh] overflow-y-auto animate-slide-up max-w-2xl mx-auto">
      {/* Handle bar */}
      <div className="sticky top-0 bg-white dark:bg-slate-900 pt-3 pb-2 px-4">
        <div className="w-12 h-1 bg-slate-300 dark:bg-slate-600 rounded-full mx-auto mb-3" />

        {/* Header with navigate and close buttons */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-bold text-slate-900 dark:text-white truncate">
              {station.brand}
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 truncate">
              {station.address}, {station.postcode}
            </p>
          </div>
          <a
            href={getDirectionsUrl(station.lat, station.lng)}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-1.5 h-10 min-h-[44px] px-4 rounded-full bg-green-600 hover:bg-green-700 text-white text-sm font-semibold transition-colors shrink-0"
          >
            <ExternalLink size={16} />
            Navigate
          </a>
          <button
            onClick={onClose}
            className="flex items-center justify-center w-10 h-10 min-h-[44px] rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors shrink-0"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>
      </div>

      <div className="px-4 pb-6">
        {/* Price and distance row */}
        <div className="flex items-center justify-between py-3 border-b border-slate-200 dark:border-slate-700">
          <div>
            <span className={`text-4xl font-bold ${priceColorClass}`}>
              {formatPrice(price)}
            </span>
            <span className="text-slate-500 dark:text-slate-400 ml-2">
              {fuelType === 'E10' ? 'Petrol' : 'Diesel'}
            </span>
          </div>
          <div className="text-right">
            <p className="text-lg font-semibold text-slate-900 dark:text-white">
              {formatDistance(station.distanceMiles)}
            </p>
            <p className="flex items-center gap-1 text-sm text-slate-500 dark:text-slate-400">
              <Clock size={14} />
              Updated {formatRelativeTime(station.updatedAt)}
            </p>
          </div>
        </div>

        {/* All fuel prices */}
        <div className="py-4 border-b border-slate-200 dark:border-slate-700">
          <h3 className="text-sm font-semibold text-slate-600 dark:text-slate-300 mb-3">
            All Prices
          </h3>
          <div className="grid grid-cols-2 gap-3">
            {(['E10', 'B7', 'E5', 'SDV'] as FuelType[]).map((ft) => (
              <div key={ft} className="flex justify-between text-base">
                <span className="text-slate-600 dark:text-slate-300">
                  {FUEL_LABELS[ft]}
                </span>
                <span
                  className={`font-semibold ${
                    ft === fuelType
                      ? priceColorClass
                      : 'text-slate-900 dark:text-white'
                  }`}
                >
                  {formatPrice(station.prices[ft])}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Price History Chart */}
        <PriceHistoryChart
          history={history ?? { dates: [], prices: {} }}
          timeRange={timeRange}
          onTimeRangeChange={setTimeRange}
          isLoading={historyLoading}
        />

      </div>
    </div>
  )
}
